import React, { useEffect, useMemo, useState } from 'react'
import { CloseAnnotationTypes, QuestionProps } from '@mission.io/question-views'
import { RangeSelectionData } from '@mission.io/question-toolkit'
import {
	addAnnotation,
	Annotations,
	deleteAnnotationRange,
	deleteAnnotationsInRange,
	getAnnotationWithStyleAlreadyCoveringRange,
	getNextId,
	updateAnnotationRange,
} from './helpers'
import { useEventTracker } from '../Markdown/hooks'
import MarkdownTextToSpeech from '../Markdown/TextToSpeechMarkdown'
import classnames from 'classnames'

export const ANNOTATION_INFORMATION_METHODS: CloseAnnotationTypes.AnnotationInformationMethods = {
	ANNOTATE: 'ANNOTATE',
	ERASE_WHOLE_ANNOTATION: 'ERASE_WHOLE_ANNOTATION',
	ERASE_RANGE: 'ERASE_RANGE',
}

// Error message types for the close annotation component
export const CLOSE_ANNOTATION_ERROR_MESSAGE_TYPES: CloseAnnotationTypes.ErrorMessageTypes = {
	ANNOTATION_LIMIT_EXCEEDED: 'ANNOTATION_LIMIT_EXCEEDED',
	UNKNOWN_ERROR: 'UNKNOWN_ERROR',
}

/**
 * StudentAnnotatableMarkdown - allows the user to add annotations to markdown
 *
 * @param {Object} props - the react props
 * @param {string} props.markdown - the markdown to annotate
 * @param {Annotations} props.annotations - the current annotations
 * @param {Annotations | null} props.readOnlyAnnotations? - annotations which should be rendered, but which can not be deleted or modified
 * @param {((newAnnotations: Annotations) => unknown) | null} props.setAnnotations - a callback used to updated the annotations, if null/undefined, the user will be unable to annotate (ie. this component becomes "read-only")
 * @param {AnnotationInformation | null} props.annotationInformation - the currently selected annotation style, if null/undefined, the user will be unable to annotate (ie. this component becomes "read-only")
 * @param {string | null} props.className - the class name to apply to the component
 * @param {string} props.selectableTextClassName? - a class name to add to any text which is electable
 * @param {TextToSpeechComponent | null} props.textToSpeech? - a component used to read the text in the markdown out loud to the student
 */
export function StudentAnnotatableMarkdown({
	markdown,
	annotations,
	annotationLimit,
	onError,
	readOnlyAnnotations,
	setAnnotations,
	annotationInformation,
	className,
	selectableTextClassName,
	textToSpeech,
}: {
	markdown: string
	annotations: Annotations
	annotationLimit?: number | null
	onError?: (error: keyof CloseAnnotationTypes.ErrorMessageTypes) => unknown
	readOnlyAnnotations?: Annotations | null
	setAnnotations?: ((newAnnotations: Annotations) => unknown) | null
	annotationInformation?: CloseAnnotationTypes.AnnotationInformation
	className?: string
	selectableTextClassName?: string
	textToSpeech?: QuestionProps.TextToSpeechComponent | null
}): JSX.Element {
	const eventTracker = useEventTracker()
	const [selectedAnnotationId, setSelectedAnnotationId] = useState<
		string | null
	>(null)

	useEffect(() => {
		if (!selectedAnnotationId) {
			return
		}
		const abortController = new AbortController()

		window.addEventListener(
			'click',
			event => {
				if (
					eventTracker.current?.firstEventTypeHandledInCurrentEvaluationLoop !=
					null
				) {
					// this is an annoying edge case, since a click event is preceded by `touchUp`, `mouseDown`, `mouseUp` we have to check if any of those events are being handled else where before knowing the click was supposed to be handled here
					return
				}
				eventTracker.current?.noteEventType(event.type)
				setSelectedAnnotationId(null)
			},
			{
				signal: abortController.signal,
			}
		)

		return () => abortController.abort()
	}, [selectedAnnotationId])

	/**
	 * getUpdatedAnnotation - get the updated annotations given the selected range information
	 *
	 * @param {RangeSelectionData} rangeSelectionData - data about the selected range
	 *
	 * @return {Annotations} - the updated annotations
	 */
	function getUpdatedAnnotation({
		range,
		rangeOverlappingNextSelectableElement,
		extendedRangeToNextSelectableElements,
	}: RangeSelectionData): Annotations {
		if (!annotationInformation) {
			return annotations
		}

		if (
			annotationInformation.method === ANNOTATION_INFORMATION_METHODS.ANNOTATE
		) {
			const newAnnotation = {
				id: getNextId(annotations),
				range: range,
				style: {
					tool: annotationInformation.tool,
					color: annotationInformation.color,
				},
			}

			const annotationAlreadyCoveringRange = getAnnotationWithStyleAlreadyCoveringRange(
				annotations,
				newAnnotation
			)
			if (annotationAlreadyCoveringRange) {
				if (selectedAnnotationId !== annotationAlreadyCoveringRange.id) {
					setSelectedAnnotationId(annotationAlreadyCoveringRange.id)
				}
				return annotations
			}

			const newAnnotations = addAnnotation(
				annotations,
				newAnnotation,
				rangeOverlappingNextSelectableElement,
				markdown.length
			)

			if (annotationLimit != null && newAnnotations.length > annotationLimit) {
				onError?.(
					CLOSE_ANNOTATION_ERROR_MESSAGE_TYPES.ANNOTATION_LIMIT_EXCEEDED
				)
				return annotations
			}

			setSelectedAnnotationId(newAnnotation.id)
			return newAnnotations
		}

		if (
			annotationInformation.method ===
			ANNOTATION_INFORMATION_METHODS.ERASE_RANGE
		) {
			return deleteAnnotationRange(annotations, {
				range: extendedRangeToNextSelectableElements,
				style: {
					tool: annotationInformation.tool,
					color: annotationInformation.color,
				},
			})
		}

		if (
			annotationInformation.method ===
			ANNOTATION_INFORMATION_METHODS.ERASE_WHOLE_ANNOTATION
		) {
			return deleteAnnotationsInRange(annotations, {
				range: extendedRangeToNextSelectableElements,
				style: {
					tool: annotationInformation.tool,
					color: annotationInformation.color,
				},
			})
		}

		return annotations
	}

	const allAnnotations: Annotations = useMemo(() => {
		if (readOnlyAnnotations != null) {
			return [...readOnlyAnnotations, ...annotations]
		}
		return annotations
	}, [readOnlyAnnotations, annotations])

	return (
		<MarkdownTextToSpeech
			className={classnames(className, 'notranslate')}
			children={markdown}
			annotations={allAnnotations}
			{...(annotationInformation
				? {
						readOnly: false,
						onRangeSelectionPreview: rangeData => [
							...getUpdatedAnnotation(rangeData),
							...(readOnlyAnnotations ?? []),
						],
						onRangeSelection: rangeData => {
							setAnnotations?.(getUpdatedAnnotation(rangeData))
						},
						onAnnotationRangeUpdatePreview: (annotationId, rangeSelection) => {
							const {
								otherAnnotations: newAnnotations,
								updatedAnnotation,
							} = updateAnnotationRange(
								annotationId,
								rangeSelection,
								annotations
							)

							if (updatedAnnotation) {
								newAnnotations.push(updatedAnnotation)
							}

							return newAnnotations
						},
						onAnnotationRangeUpdate: (annotationId, rangeSelection) => {
							if (!setAnnotations) {
								return
							}

							const {
								otherAnnotations: newAnnotations,
								updatedAnnotation,
							} = updateAnnotationRange(
								annotationId,
								rangeSelection,
								annotations
							)

							if (!updatedAnnotation) {
								return
							}

							setAnnotations(
								addAnnotation(
									newAnnotations,
									updatedAnnotation,
									rangeSelection.rangeOverlappingNextSelectableElement,
									markdown.length
								)
							)
						},
						selectedAnnotationId: selectedAnnotationId,
						eventTrackerRef: eventTracker,
				  }
				: { readOnly: true })}
			selectableTextClassName={selectableTextClassName}
			textToSpeech={textToSpeech}
		/>
	)
}
